import type { EChartsOption, SankeySeriesOption } from 'echarts';

import { buildAnimationConfig } from '../shared/animation';
import { buildColorDot, buildTooltipConfig, buildTooltipDivider } from '../shared/tooltip';
import type { EChartsTheme } from '../types';
import type { SankeyChartConfig, SankeyChartDatum } from './types';

export type { SankeyChartConfig, SankeyChartDatum } from './types';

const DEFAULT_LINK_OPACITY = 0.24;
const DIMMED_LINK_OPACITY = 0.2;
const HIGHLIGHTED_LINK_OPACITY = 0.85;
const DIMMED_NODE_OPACITY = 0.35;
// Matches the label clearance provided by the legacy chart's `paddingRight`.
const RIGHT_LABEL_PADDING = 200;
/** Space reserved for each node's two-line label plus a small visual gap. */
const NODE_LABEL_SLOT_HEIGHT = 48;
const MIN_CHART_HEIGHT = 480;
const NODE_GAP = 42;
// A short top node centers its two-line label around the node. Reserve a full
// label slot above and below the plot so that label cannot be clipped.
const VERTICAL_PADDING = NODE_LABEL_SLOT_HEIGHT;

// Node names can be user-entered (payee, tag, category names) and are interpolated into
// tooltip HTML rendered via innerHTML — escape to prevent HTML/script injection.
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const CLICK_CURSOR_ICON_INLINE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" aria-hidden="true"><g stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.444"><path d="m16.59 17.346 1.527 3.017a.554.554 0 0 1-.244.744l-1.646.833a.554.554 0 0 1-.744-.244l-1.527-3.017-2.632 1.647.006-9.135c0-.456.521-.716.886-.442l7.258 5.452z"/><path d="M5 11H3M21 11h-2M7.05 15.95l-1.41 1.41M18.36 4.64l-1.41 1.41M12 2v2M5.64 4.64l1.41 1.41"/></g></svg>';

const buildSankeyTooltipCard = (
  name: string,
  color: string,
  amount: string,
  percent: string,
  percentSuffix: string,
  count: number,
  theme: EChartsTheme,
): string => {
  const header = `<div style="display:flex;align-items:center;gap:6px;">${buildColorDot(color)}<span style="color:${theme.textColor};font-size:13px;font-weight:500;">${escapeHtml(name)}</span></div>`;
  const body = `<div style="color:${theme.textSecondaryColor};font-size:12px;margin-top:4px;">${amount} (${percent}% ${percentSuffix})</div>`;
  if (count <= 0) return `${header}${body}`;
  const plural = count === 1 ? 'transaction' : 'transactions';
  const footer = `<div style="color:${theme.textTertiaryColor};font-size:12px;display:flex;align-items:center;gap:8px;"><span aria-hidden="true" style="display:inline-flex;width:18px;height:18px;color:'inherit';">${CLICK_CURSOR_ICON_INLINE_SVG}</span>View ${count} ${plural}</div>`;
  return `${header}${body}${buildTooltipDivider(theme)}${footer}`;
};

type SankeyTooltipParams = {
  dataType?: 'node' | 'edge';
  data?: { source?: string; target?: string; value?: number };
  name?: string;
  value?: number;
};

export type SankeyNode = {
  name: string;
  value: number;
  /** The value to use for this node's label/tooltip amount + percent — `value` unless some incoming flow set `overrideValue`. */
  labelValue: number;
  column: number;
};

export const getIncomeNodeNames = (data: SankeyChartDatum[], totalIncomeNode: string): Set<string> => {
  const names = new Set<string>([totalIncomeNode]);
  let changed = true;
  while (changed) {
    changed = false;
    data.forEach(({ from, to }) => {
      if (names.has(to) && !names.has(from)) {
        names.add(from);
        changed = true;
      }
    });
  }
  return names;
};

export const buildSankeyGraph = (
  data: SankeyChartDatum[],
): { nodes: SankeyNode[]; nodesByName: Map<string, SankeyNode>; columnTotals: Map<number, number> } => {
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  const parents = new Map<string, Set<string>>();
  const labelValueOverrides = new Map<string, number>();

  data.forEach(({ from, to, value, overrideValue }) => {
    outgoing.set(from, (outgoing.get(from) ?? 0) + value);
    incoming.set(to, (incoming.get(to) ?? 0) + value);
    if (overrideValue !== undefined) labelValueOverrides.set(to, overrideValue);
    const nodeParents = parents.get(to) ?? new Set<string>();
    nodeParents.add(from);
    parents.set(to, nodeParents);
  });

  const names = new Set([...incoming.keys(), ...outgoing.keys()]);
  const columns = new Map<string, number>();
  names.forEach((name) => {
    if (!incoming.has(name)) columns.set(name, 0);
  });

  while (columns.size < names.size) {
    let progressed = false;
    names.forEach((name) => {
      if (columns.has(name)) return;
      const nodeParents = parents.get(name);
      if (!nodeParents) return;
      const parentColumns = [...nodeParents].map((parent) => columns.get(parent));
      if (parentColumns.every((column): column is number => column !== undefined)) {
        columns.set(name, Math.max(...parentColumns) + 1);
        progressed = true;
      }
    });
    if (!progressed) {
      names.forEach((name) => {
        if (!columns.has(name)) columns.set(name, 0);
      });
    }
  }

  const columnTotals = new Map<number, number>();
  const nodes = [...names].map((name) => {
    const column = columns.get(name) ?? 0;
    const value = Math.max(incoming.get(name) ?? 0, outgoing.get(name) ?? 0);
    columnTotals.set(column, (columnTotals.get(column) ?? 0) + value);
    return { name, value, labelValue: labelValueOverrides.get(name) ?? value, column };
  });

  return { nodes, nodesByName: new Map(nodes.map((node) => [node.name, node])), columnTotals };
};

/**
 * ECharts sizes narrow nodes from their values, so small nodes alone cannot
 * reserve enough room for their labels. Size the chart from the busiest
 * column, which lets every node retain a readable label slot.
 */
export const getSankeyChartHeight = (data: SankeyChartDatum[]): number => {
  const { nodes } = buildSankeyGraph(data);
  const nodesByColumn = new Map<number, number>();
  nodes.forEach(({ column }) => nodesByColumn.set(column, (nodesByColumn.get(column) ?? 0) + 1));
  const maxColumnNodeCount = Math.max(0, ...nodesByColumn.values());

  return Math.max(MIN_CHART_HEIGHT, maxColumnNodeCount * NODE_LABEL_SLOT_HEIGHT + VERTICAL_PADDING * 2);
};

export const buildSankeyChartOption = (data: SankeyChartDatum[], config: SankeyChartConfig): EChartsOption => {
  const {
    theme,
    animated = true,
    formatAmount,
    privacyMask,
    selectedNodeName,
    incomeNodeNames,
    incomeColor,
    hiddenNodeNames,
    percentageTotal,
    chartHeight = MIN_CHART_HEIGHT,
    hideCents = false,
    transactionCountByNodeName,
    nodeColorOverrides,
    incomeRelativePercentNodeNames,
    leftAlignExpenseLabels = false,
    selectionDim = true,
  } = config;
  const amountFormat = hideCents ? '0,0' : '0,0.00';
  const { nodes, nodesByName, columnTotals } = buildSankeyGraph(data);
  const maxColumn = Math.max(0, ...nodes.map(({ column }) => column));
  const minimumNodeValue = Math.max(...columnTotals.values(), 0) / Math.max(chartHeight - VERTICAL_PADDING * 2, 1);
  const connectedNames = new Set<string>();
  if (selectedNodeName) {
    connectedNames.add(selectedNodeName);
    data.forEach(({ from, to }) => {
      if (from === selectedNodeName) connectedNames.add(to);
      if (to === selectedNodeName) connectedNames.add(from);
    });
  }

  const getNodeDetails = (nodeName: string): { amount: string; percent: string } | undefined => {
    const node = nodesByName.get(nodeName);
    if (!node) return undefined;

    const value = node.labelValue;
    return {
      amount: privacyMask(formatAmount(value, 'USD', amountFormat)),
      percent: ((value / (percentageTotal ?? columnTotals.get(node.column) ?? 1)) * 100).toFixed(1),
    };
  };

  const nodeColorByName = new Map<string, string>();
  const seriesData = nodes.map((node, index) => {
    const color =
      nodeColorOverrides?.[node.name] ??
      (incomeNodeNames.has(node.name) ? incomeColor : theme.colors[index % theme.colors.length]);
    nodeColorByName.set(node.name, color);
    return {
      name: node.name,
      // ECharts has no min-node-size option. Preserve the raw amount in the
      // label and tooltip, but reserve a one-pixel bar for nonzero tiny nodes.
      value: node.value > 0 ? Math.max(node.value, minimumNodeValue) : node.value,
      itemStyle: {
        color,
        opacity: !selectionDim || !selectedNodeName || connectedNames.has(node.name) ? 1 : DIMMED_NODE_OPACITY,
      },
      // Per-item label override — merges with the series-level label below, so only
      // `position` needs to be set here to flip the rightmost column's labels leftward.
      ...(leftAlignExpenseLabels && node.column === maxColumn ? { label: { position: 'left' as const } } : {}),
    };
  });

  const series: SankeySeriesOption = {
    type: 'sankey',
    left: 8,
    // Left-aligned expense labels render inside the rightmost nodes rather than past
    // them, so the chart no longer needs to reserve external space for them there.
    right: leftAlignExpenseLabels ? 8 : RIGHT_LABEL_PADDING,
    top: VERTICAL_PADDING,
    bottom: VERTICAL_PADDING,
    nodeAlign: 'left',
    nodeWidth: 18,
    // ECharts has no `minNodeSize` equivalent. Reserve enough gap for labels
    // on tiny leaves without inflating flow values and distorting link proportions.
    nodeGap: NODE_GAP,
    draggable: false,
    // ECharts starts each column at the top, then uses relaxation iterations to
    // vertically center nodes around their links. Keep the legacy top-stacked layout.
    layoutIterations: 0,
    emphasis: { focus: 'adjacency' },
    lineStyle: { color: 'gradient', opacity: DEFAULT_LINK_OPACITY, curveness: 0.5 },
    label: {
      color: theme.textColor,
      fontSize: 14,
      formatter: ({ name }: { name: string }): string => {
        if (hiddenNodeNames?.has(name)) return '';
        const details = getNodeDetails(name);
        if (!details) return name;
        return `{name|${name}}\n{amount|${details.amount}} {percent|(${details.percent}% )}`;
      },
      rich: {
        name: { color: theme.textColor, fontSize: 14, lineHeight: 18 },
        amount: { color: theme.textColor, fontSize: 14, fontWeight: 'bold', lineHeight: 16 },
        percent: { color: theme.textColor, fontSize: 14, fontWeight: 'bold', lineHeight: 16 },
      },
    },
    data: seriesData,
    links: data.map(({ from, to, value }) => ({
      source: from,
      target: to,
      value,
      lineStyle: selectionDim
        ? {
            opacity: !selectedNodeName
              ? DEFAULT_LINK_OPACITY
              : from === selectedNodeName || to === selectedNodeName
                ? HIGHLIGHTED_LINK_OPACITY
                : DIMMED_LINK_OPACITY,
          }
        : undefined,
    })),
  };

  return {
    ...buildAnimationConfig(animated),
    textStyle: { fontFamily: theme.fontFamily },
    tooltip: {
      ...buildTooltipConfig(theme),
      trigger: 'item',
      formatter: (params: unknown): string => {
        const item = params as SankeyTooltipParams;
        const nodeName = item.dataType === 'edge' ? (item.data?.target ?? '') : (item.name ?? '');
        if (!nodeName || hiddenNodeNames?.has(nodeName)) return '';
        const details = getNodeDetails(nodeName);
        if (!details) return nodeName;
        const nodeColor = nodeColorByName.get(nodeName) ?? theme.colors[0];
        const count = transactionCountByNodeName?.[nodeName] ?? 0;
        const percentSuffix =
          incomeNodeNames.has(nodeName) || incomeRelativePercentNodeNames?.has(nodeName) ? 'of income' : 'of spend';
        return buildSankeyTooltipCard(
          nodeName,
          nodeColor,
          details.amount,
          details.percent,
          percentSuffix,
          count,
          theme,
        );
      },
    },
    series: [series],
  };
};
